if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      isDirectCheckout() {
        return this.dataset.directCheckout === 'true';
      }

      getCartAttributes() {
        const attributes = {};
        this.form.querySelectorAll('[name^="attributes["]').forEach((input) => {
          const match = input.name.match(/^attributes\[(.+)\]$/);
          if (!match) return;

          let value = input.value;
          if (input.type === 'checkbox') {
            value = input.checked ? input.value : '';
          }

          attributes[match[1]] = value;
        });

        return attributes;
      }

      getDirectCheckoutFormData() {
        const formData = new FormData(this.form);
        const allowedKeys = ['id', 'quantity', 'selling_plan'];

        Array.from(formData.keys()).forEach((key) => {
          if (allowedKeys.includes(key) || key.startsWith('properties[')) {
            return;
          }
          formData.delete(key);
        });

        if (!formData.get('quantity')) {
          formData.set('quantity', '1');
        }

        return formData;
      }

      updateCartAttributes(attributes) {
        if (!attributes || Object.keys(attributes).length === 0) {
          return Promise.resolve();
        }

        const body = JSON.stringify({ attributes });
        return fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } }).then((response) => {
          if (!response.ok) {
            throw new Error('Failed to update cart attributes');
          }
          return response;
        });
      }

      clearCartForDirectCheckout() {
        return fetch(`${routes.cart_url}/clear.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          cache: 'no-store',
        }).then((response) => {
          if (!response.ok) {
            throw new Error('Failed to clear cart');
          }
          return response;
        });
      }

      addItemForDirectCheckout(formData) {
        return fetch(`${routes.cart_add_url}.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: formData,
        }).then(async (response) => {
          if (response.ok) {
            return response.json();
          }

          let errorMessage = 'Failed to add item to cart';
          try {
            const errorData = await response.json();
            errorMessage = errorData.description || errorData.message || errorMessage;
          } catch (error) {
            console.error(error);
          }

          throw new Error(errorMessage);
        });
      }

      submitDirectCheckout() {
        const checkoutForm = document.createElement('form');
        checkoutForm.method = 'post';
        checkoutForm.action = window.routes.cart_url;

        const checkoutInput = document.createElement('input');
        checkoutInput.type = 'hidden';
        checkoutInput.name = 'checkout';
        checkoutInput.value = 'checkout';

        checkoutForm.appendChild(checkoutInput);
        document.body.appendChild(checkoutForm);
        checkoutForm.submit();
      }

      onDirectCheckoutHandler() {
        const cartAttributes = this.getCartAttributes();
        const formData = this.getDirectCheckoutFormData();

        if (!cartAttributes.bonusQuantity) {
          cartAttributes.bonusQuantity = formData.get('quantity') || '1';
        }

        return this.clearCartForDirectCheckout()
          .then(() => this.addItemForDirectCheckout(formData))
          .then(() => this.updateCartAttributes(cartAttributes))
          .then(() => {
            this.error = false;
            this.submitDirectCheckout();
          });
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        if (this.isDirectCheckout()) {
          this.onDirectCheckoutHandler()
            .catch((error) => {
              this.error = true;
              this.handleErrorMessage(error.message || 'Checkout failed');
              console.error(error);
            })
            .finally(() => {
              this.submitButton.classList.remove('loading');
              this.submitButton.removeAttribute('aria-disabled');
              this.querySelector('.loading__spinner').classList.add('hidden');
            });
          return;
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const cartAttributes = this.getCartAttributes();
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            return this.updateCartAttributes(cartAttributes).then(() => {
              if (!this.error)
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  cartData: response,
                });
              this.error = false;
              const quickAddModal = this.closest('quick-add-modal');
              if (quickAddModal) {
                document.body.addEventListener(
                  'modalClosed',
                  () => {
                    setTimeout(() => {
                      this.cart.renderContents(response);
                    });
                  },
                  { once: true }
                );
                quickAddModal.hide(true);
              } else {
                this.cart.renderContents(response);
              }
            });
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
